import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Layout, Menu, Typography, Button } from 'antd';
import type { MenuProps } from 'antd';
import {
    ShopOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useRbac } from '@/hooks/useRbac';
import { usePlan } from '@/hooks/usePlan';
import { useBusinessInfo } from '@/hooks/useTenantSettings';
import { useFulfillmentGeneralSettings } from '@/hooks/use-fulfillment-settings';
import { buildSidebarMenuItems } from './menuItems';
import { MenuLabel } from './MenuLabel';
import { accountingVoucherSourceRoute } from '@/constants/routes';
import { userRolesOf } from '@/utils/userRoles';

type SidebarItems = NonNullable<MenuProps['items']>;
type SidebarItem = SidebarItems[number];

function hasMenuChildren(
    item: SidebarItem,
): item is SidebarItem & { children: SidebarItems } {
    return !!item && 'children' in item && Array.isArray((item as { children?: unknown }).children);
}

/**
 * Bọc nhãn menu để tên bị cắt cụt còn hiện được tooltip (TCR-1215).
 *
 * Chỉ bọc nhãn dạng chuỗi. Nhãn tự dựng (nhóm kế toán có chấm màu) đã có bố cục
 * riêng và tự bọc lấy phần chữ của nó.
 */
function withEllipsisTooltip(label: React.ReactNode): React.ReactNode {
    if (typeof label !== 'string') return label;
    return <MenuLabel text={label} />;
}

export function stripSidebarMenuMetadata(items: SidebarItems): SidebarItems {
    return items.map((item) => {
        if (!item || (item as { type?: string }).type === 'divider') return item;
        const { drilldown: _drilldown, children, ...rest } = item as SidebarItem & {
            drilldown?: boolean;
            children?: SidebarItems;
        };
        const label = (rest as { label?: React.ReactNode }).label;
        return {
            ...rest,
            ...(label !== undefined ? { label: withEllipsisTooltip(label) } : {}),
            ...(Array.isArray(children) ? { children: stripSidebarMenuMetadata(children) } : {}),
        } as SidebarItem;
    });
}

function normalizeMenuKey(key: string): string {
    const withoutScopedSuffix = key.split('__')[0];
    return withoutScopedSuffix.endsWith('-root')
        ? withoutScopedSuffix.slice(0, -'-root'.length)
        : withoutScopedSuffix;
}

function isModifiedLinkClick(event: React.MouseEvent<HTMLAnchorElement>): boolean {
    return (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
    );
}

export function withSidebarLinks(
    items: SidebarItems,
    onNavigate: (path: string) => void,
): SidebarItems {
    return items.map((item) => {
        if (!item || (item as { type?: string }).type === 'divider') return item;

        const menuItem = item as SidebarItem & {
            children?: SidebarItems;
            label?: React.ReactNode;
            onClick?: unknown;
        };

        if (Array.isArray(menuItem.children)) {
            return {
                ...menuItem,
                children: withSidebarLinks(menuItem.children, onNavigate),
            } as SidebarItem;
        }

        if (!menuItem.key || menuItem.label === undefined) return item;

        const path = normalizeMenuKey(String(menuItem.key));
        if (!path.startsWith('/')) return item;

        const { onClick: _onClick, ...itemWithoutClick } = menuItem;
        return {
            ...itemWithoutClick,
            label: (
                <Link
                    to={path}
                    className="erp-sidebar-link"
                    onClick={(event) => {
                        if (isModifiedLinkClick(event)) return;
                        event.preventDefault();
                        onNavigate(path);
                    }}
                >
                    {menuItem.label}
                </Link>
            ),
        } as SidebarItem;
    });
}

interface MenuKeyEntry {
    rawKey: string;
    normalizedPath: string;
}

function collectMenuKeys(items: NonNullable<MenuProps['items']>): MenuKeyEntry[] {
    const keys: MenuKeyEntry[] = [];
    for (const item of items) {
        if (!item || (item as { type?: string }).type === 'divider') continue;
        if ('key' in item && item.key) {
            const rawKey = String(item.key);
            keys.push({ rawKey, normalizedPath: normalizeMenuKey(rawKey) });
        }
        const children = (item as SidebarItem & { children?: NonNullable<MenuProps['items']> }).children;
        if (Array.isArray(children)) {
            keys.push(...collectMenuKeys(children));
        }
    }
    return keys;
}

function findBestSelectedKey(pathname: string, items: NonNullable<MenuProps['items']>): string {
    const keys = collectMenuKeys(items);
    const cleanPath = pathname.replace(/\/+$/, '');
    let best = '/';
    let bestLen = 1;
    let exactMatch: string | null = null;
    for (const entry of keys) {
        if (entry.normalizedPath === cleanPath) {
            if (!exactMatch) {
                exactMatch = entry.rawKey;
            } else {
                const currentIsScoped = entry.rawKey.includes('__');
                const bestIsScoped = exactMatch.includes('__');
                if (currentIsScoped && !bestIsScoped) {
                    exactMatch = entry.rawKey;
                }
            }
            continue;
        }
        if (
            entry.normalizedPath !== '/' &&
            cleanPath.startsWith(entry.normalizedPath + '/') &&
            entry.normalizedPath.length > bestLen
        ) {
            best = entry.rawKey;
            bestLen = entry.normalizedPath.length;
        }
    }
    if (exactMatch) return exactMatch;
    return best;
}

function itemContainsKey(item: SidebarItem, leafKey: string): boolean {
    if (!item || (item as { type?: string }).type === 'divider') return false;
    if ('key' in item && item.key && String(item.key) === leafKey) return true;
    const children = (item as SidebarItem & { children?: NonNullable<MenuProps['items']> }).children;
    return Array.isArray(children) && children.some((child) => itemContainsKey(child, leafKey));
}

/**
 * Given a leaf key and a menu tree, return the full chain of ancestor
 * submenu keys (outer → inner) that must be in `openKeys` for the submenu
 * containing that leaf to be expanded. Works at any nesting depth.
 */
function findOpenKeysPath(leafKey: string, items: SidebarItems): string[] {
    for (const item of items) {
        if (!item || (item as { type?: string }).type === 'divider') continue;
        if (!('key' in item) || !item.key) continue;
        const children = (item as SidebarItem & { children?: SidebarItems }).children;
        if (!Array.isArray(children)) continue;
        if (!children.some((child) => itemContainsKey(child, leafKey))) continue;
        return [String(item.key), ...findOpenKeysPath(leafKey, children)];
    }
    return [];
}

/**
 * Given a leaf key and the top-level menu items, find the parent group key
 * whose children tree contains that leaf key. Returns null if not found.
 */
function findParentGroupKey(
    leafKey: string,
    items: NonNullable<MenuProps['items']>,
): string | null {
    for (const item of items) {
        if (!item) continue;
        if (!('children' in item) || !Array.isArray(item.children)) continue;
        const hasChild = (item.children as NonNullable<MenuProps['items']>).some((child) =>
            itemContainsKey(child, leafKey),
        );
        if (hasChild && item.key) {
            return String(item.key);
        }
    }
    return null;
}

const { Sider } = Layout;
const { Text } = Typography;

interface SidebarProps {
    collapsed?: boolean;
    onCollapse?: (collapsed: boolean) => void;
    isMobile?: boolean;
    onNavigate?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
    collapsed = false,
    onCollapse,
    isMobile = false,
    onNavigate,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { hasPermission } = useRbac();
    const { tier: planTier } = usePlan();
    const { data: businessInfo } = useBusinessInfo();
    const { data: fulfillmentGeneralSettings } = useFulfillmentGeneralSettings();
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const businessName = businessInfo?.name?.trim() || 'TCR-ERP';
    const businessLogo = businessInfo?.logo?.trim();
    const bulkPackingEnabled = fulfillmentGeneralSettings?.data.bulk_packing_enabled === true;

    // handleNavigate must be declared BEFORE menuItems construction
    const handleNavigate = useCallback((path: string) => {
        navigate(path);
        onNavigate?.();
    }, [navigate, onNavigate]);

    // Build menuItems BEFORE useEffect so it can reference menuItems
    const menuItems = useMemo(
        () => buildSidebarMenuItems({
            hasPermission,
            onNavigate: handleNavigate,
            userRole: user?.role,
            userRoles: userRolesOf(user),
            planTier,
            bulkPackingEnabled,
        }),
        [bulkPackingEnabled, handleNavigate, hasPermission, planTier, user?.role, user?.roles],
    );

    const displayedMenuItems = useMemo<SidebarItems>(
        () => withSidebarLinks(stripSidebarMenuMetadata(menuItems), handleNavigate),
        [handleNavigate, menuItems],
    );
    const topLevelSubmenuKeys = useMemo(() => new Set(
        menuItems
            .filter((item) => hasMenuChildren(item))
            .map((item) => String((item as { key: React.Key }).key)),
    ), [menuItems]);

    // leafKey + parentGroupKey must be declared BEFORE useEffect that depends on them
    const leafKey = useMemo(
        () =>
            accountingVoucherSourceRoute(location.pathname, location.search) ??
            findBestSelectedKey(location.pathname, menuItems),
        [location.pathname, location.search, menuItems],
    );
    const selectedKeys = useMemo(() => {
        const parentKey = findParentGroupKey(leafKey, menuItems);
        return parentKey ? [leafKey, parentKey] : [leafKey];
    }, [leafKey, menuItems]);

    const handleOpenChange = useCallback((nextOpenKeys: string[]) => {
        const latestTopLevelKey = nextOpenKeys.find(
            (key) => topLevelSubmenuKeys.has(key) && !openKeys.includes(key),
        );
        if (!latestTopLevelKey) {
            setOpenKeys(nextOpenKeys);
            return;
        }
        const latestTopLevelItem = menuItems.find(
            (item) => !!item && 'key' in item && String(item.key) === latestTopLevelKey,
        );
        const latestTopLevelChildren = latestTopLevelItem && hasMenuChildren(latestTopLevelItem) ? latestTopLevelItem.children : [];
        setOpenKeys(nextOpenKeys.filter((key) => {
            if (key === latestTopLevelKey) return true;
            if (topLevelSubmenuKeys.has(key)) return false;
            return latestTopLevelChildren.some((child) => itemContainsKey(child, key));
        }));
    }, [menuItems, openKeys, topLevelSubmenuKeys]);

    // Whenever the menu is rendered inline, auto-expand the full submenu chain that
    // contains the current route, so landing directly on a nested page (e.g.
    // /sales/e-invoices inside Kế toán → Hóa đơn) shows it expanded right away
    // instead of requiring a manual click to reveal it.
    useEffect(() => {
        if (collapsed || !leafKey) return;
        const openPath = findOpenKeysPath(leafKey, displayedMenuItems);
        if (openPath.length === 0) return;
        setOpenKeys((prev) => {
            const same = prev.length === openPath.length && prev.every((key, i) => key === openPath[i]);
            return same ? prev : openPath;
        });
    }, [location.pathname, collapsed, leafKey, displayedMenuItems]);

    return (
        <Sider
            trigger={null}
            collapsible
            collapsed={collapsed}
            onCollapse={onCollapse}
            width={240}
            collapsedWidth={isMobile ? 0 : 80}
            theme="dark"
            style={{
                background: 'var(--sidebar-bg)',
                boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
                zIndex: isMobile ? 100 : 98,
                position: isMobile ? 'fixed' : 'sticky',
                left: 0,
                top: 0,
                bottom: 0,
                height: isMobile ? '100dvh' : '100vh',
                paddingBottom: 'env(safe-area-inset-bottom)',
            }}
            // AntD Sider wraps content in `.ant-layout-sider-children` — force flex column there
            // so the menu can overflow-auto while logo + user info stay pinned.
            className="erp-sidebar"
        >
            <style>{`
        .erp-sidebar > .ant-layout-sider-children {
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
        }
        /* Hover effect — light bg on items in dark sidebar */
        .erp-sidebar .ant-menu-dark .ant-menu-item:hover,
        .erp-sidebar .ant-menu-dark .ant-menu-submenu-title:hover {
          background-color: rgba(255, 255, 255, 0.08) !important;
        }
        /* Selected parent group — highlight submenu title when a child is active */
        .erp-sidebar .ant-menu-dark .ant-menu-submenu-selected > .ant-menu-submenu-title {
          background-color: rgba(255, 255, 255, 0.12) !important;
        }
        .erp-sidebar .erp-sidebar-link {
          display: block;
          min-width: 0;
          color: inherit;
        }
        .erp-sidebar .erp-sidebar-link:hover {
          color: inherit;
        }
        /* Popup submenu (hover flyout) — match sidebar dark theme */
        .ant-menu-submenu-popup.ant-menu-dark > .ant-menu {
          background: var(--sidebar-bg);
          border-radius: 8px;
          box-shadow: 4px 0 16px rgba(0, 0, 0, 0.25);
          min-width: 200px;
          padding: 4px 0;
        }
        .ant-menu-submenu-popup.ant-menu-dark .ant-menu-item:hover {
          background-color: rgba(255, 255, 255, 0.08) !important;
        }
        .ant-menu-submenu-popup.ant-menu-dark .ant-menu-item-selected {
          background-color: var(--sidebar-active-bg, #a4193d) !important;
        }
        /* Multi-column popup cho group có nhiều children — giảm chiều dài
           popup khi hover. Dùng CSS columns vì antd Menu vertical popup
           render danh sách dạng flat <li>, columns tự chia. */
        .ant-menu-submenu-popup.erp-menu-popup-wide > .ant-menu {
          column-count: 2;
          column-gap: 0;
          width: 480px;
          max-height: 80vh;
          overflow-y: auto;
        }
        .ant-menu-submenu-popup.erp-menu-popup-wide > .ant-menu > .ant-menu-item,
        .ant-menu-submenu-popup.erp-menu-popup-wide > .ant-menu > .ant-menu-item-divider {
          break-inside: avoid;
        }
      /* Kế toán dùng sections cấp 2 để tránh mega menu quá cao. */
        .ant-menu-submenu-popup.erp-menu-popup-accounting > .ant-menu {
          width: 260px;
          max-height: min(520px, calc(100vh - 24px));
          overflow-y: auto;
          padding: 6px;
        }
        .ant-menu-submenu-popup.erp-menu-popup-accounting > .ant-menu > .ant-menu-submenu {
          margin: 2px 0;
        }
        .ant-menu-submenu-popup.erp-menu-popup-accounting > .ant-menu > .ant-menu-submenu > .ant-menu-submenu-title {
          margin: 0;
          width: 100%;
          height: 38px;
          line-height: 38px;
          border-left: 3px solid rgba(255, 255, 255, 0.12);
          border-radius: 6px;
          padding-inline: 12px 28px !important;
        }
        .ant-menu-submenu-popup.erp-menu-popup-accounting > .ant-menu > .ant-menu-submenu > .ant-menu-submenu-title:hover {
          background-color: rgba(255, 255, 255, 0.08) !important;
        }
        .ant-menu-submenu-popup.erp-menu-popup-accounting-section > .ant-menu {
          width: 260px;
          max-height: min(520px, calc(100vh - 24px));
          overflow-y: auto;
          padding: 6px;
        }
        .ant-menu-submenu-popup.erp-menu-popup-accounting-section .ant-menu-item {
          margin: 2px 0;
          width: 100%;
          border-radius: 6px;
          padding-inline: 12px !important;
        }
        .erp-accounting-section-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
          color: rgba(255, 255, 255, 0.9);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }
        .erp-accounting-section-dot {
          width: 7px;
          height: 7px;
          border-radius: 999px;
          box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.08);
          flex-shrink: 0;
        }
        .erp-sidebar .ant-menu-inline .erp-accounting-section-label {
          color: rgba(255, 255, 255, 0.9);
        }
      `}</style>
            {/* Logo */}
            <div
                style={{
                    height: 64,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: collapsed ? '0 8px' : '0 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    {businessLogo ? (
                        <img
                            src={businessLogo}
                            alt={businessName}
                            style={{
                                width: 28,
                                height: 28,
                                borderRadius: 6,
                                objectFit: 'cover',
                                background: '#fff',
                                flexShrink: 0,
                            }}
                        />
                    ) : (
                        <ShopOutlined style={{ fontSize: 24, color: 'var(--color-primary)', flexShrink: 0 }} />
                    )}
                    {!collapsed && (
                        <Text
                            strong
                            style={{
                                fontSize: 15,
                                color: '#fff',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {businessName}
                        </Text>
                    )}
                </div>
                <Button
                    type="text"
                    size="small"
                    icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    onClick={() => onCollapse?.(!collapsed)}
                    style={{ color: '#fff', fontSize: 14, padding: 4 }}
                />
            </div>

            {/* Navigation Menu — scrollable region */}
            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                    paddingBottom: isMobile ? 'calc(env(safe-area-inset-bottom) + 12px)' : undefined,
                }}
            >
                <Menu
                    // Expanded sidebar → inline accordion like AMIS/MISA: parent stays visible
                    // and children open below it. Collapsed rail stays uncontrolled so AntD can
                    // show popup children from icon-only items.
                    {...(collapsed
                        ? { mode: 'vertical' as const }
                        : { mode: 'inline' as const, openKeys, onOpenChange: handleOpenChange })}
                    theme="dark"
                    selectedKeys={selectedKeys}
                    items={displayedMenuItems}
                    triggerSubMenuAction="hover"
                    subMenuOpenDelay={0.1}
                    style={{
                        background: 'var(--sidebar-bg)',
                        border: 'none',
                    }}
                />
            </div>
        </Sider>
    );
};
